/* eslint-disable camelcase */
import React from 'react';

import styled from '@emotion/styled';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Bar, BarChart, Legend, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

type StockoutProps = {
  data?: {
    period: string;
    total_missed_revenue: string;
    total_sale: string;
  }[];
  isLoading?: boolean;
  isError?: boolean;
};

const CustomTooltip = ({ active, payload = [] }: TooltipProps<ValueType, NameType>) => {
  if (!active || payload.length === 0) return null;
  return (
    <div
      style={{
        backgroundColor: '#020203',
        textAlign: 'right',
        padding: '10px 5px',
        borderRadius: '4px',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      }}
    >
      {payload.map((pld) => {
        if (pld && typeof pld.name === 'string' && !pld.name.endsWith('area')) {
          return (
            <p
              key={pld.name}
              style={{
                color: '#fff',
                padding: 0,
                margin: '2px',
                fontSize: '12px',
                fontWeight: 'normal',
              }}
            >
              {pld.name === 'total_missed_revenue' ? `Missed sales` : 'Actual sales'}:{' '}
              {typeof pld.value === 'number' ||
              (typeof pld.value === 'string' && !isNaN(Number(pld.value)))
                ? Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }).format(Number(pld.value))
                : ''}
            </p>
          );
        }
        return null;
      })}
    </div>
  );
};

const CustomLegend = ({ payload = [] }: TooltipProps<ValueType, NameType>) => {
  if (payload.length === 0) return null;
  return (
    <Box
      style={{
        marginTop: '-25px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: '20px',
      }}
    >
      {payload.map((entry) => {
        if (entry && typeof entry.value === 'string' && !entry.value.endsWith('area')) {
          return (
            <Box key={entry.value} style={{ display: 'flex', flexDirection: 'row' }}>
              <Box
                style={{
                  width: '9px',
                  height: '9px',
                  backgroundColor: entry.color,
                  marginRight: '6px',
                  marginTop: '3px',
                }}
              />
              <Box style={{ color: '#000', fontSize: '12px' }}>
                {entry.value === 'total_missed_revenue' ? 'Missed Sales ' : 'Actual sales'}
              </Box>
            </Box>
          );
        }
        return null;
      })}
    </Box>
  );
};

const CustomBoxContainer = styled(Box)`
  padding: 10px;
`;

export default function StockoutChart({ data, isError, isLoading }: StockoutProps) {
  if (isLoading || isError || !data || data.length === 0) {
    return (
      <Flex alignItems="center" justifyContent="center" width={520} height={382}>
        <Box alignItems="center">
          {isLoading && <Spinner data-test="spinner" size="2rem" color="gray-300" />}
          {!isLoading && <Box data-test="error">Error loading data</Box>}
        </Box>
      </Flex>
    );
  }

  // We cast the total_missed_revenue and total_sale to numbers
  const formattedData = data.map((d) => ({
    ...d,
    total_cost: isNaN(Number(d.total_missed_revenue)) ? 0 : Number(d.total_missed_revenue),
    total_sale: isNaN(Number(d.total_sale)) ? 0 : Number(d.total_sale),
  }));

  return (
    <CustomBoxContainer data-test="chart">
      <BarChart
        width={520}
        height={342}
        data={formattedData}
        margin={{
          top: 40,
          right: 30,
          left: 10,
          bottom: 30,
        }}
      >
        <XAxis
          dataKey="period"
          label={{
            value: 'Month - 2023 through 2024',
            angle: 0,
            position: 'insideBottom',
            offset: -25,
            fontWeight: 'bold',
            fill: 'black',
            fontSize: '12px',
          }}
          tickLine={false}
          tick={{
            fontSize: '12px',
            dy: 5,
          }}
        />

        <YAxis
          label={{
            value: 'Dollars ($)',
            angle: -90,
            offset: 0,
            position: 'insideLeft',
            fontWeight: 'bold',
            fill: 'black',
            fontSize: '12px',
          }}
          domain={['auto', 'auto']}
          tickFormatter={(value) => {
            if (value === 0) {
              return '';
            }
            return Intl.NumberFormat('en-US', {
              notation: 'compact',
              compactDisplay: 'short',
            }).format(value);
          }}
          tick={{
            fontSize: '12px',
          }}
        />
        <Tooltip cursor={false} content={<CustomTooltip />} />

        <Legend verticalAlign="top" content={<CustomLegend />} />

        <Bar dataKey="total_sale" stackId="a" fill="#D7DCE1" barSize="14" />
        <Bar dataKey="total_missed_revenue" stackId="a" fill="#DE2E37" />
      </BarChart>
    </CustomBoxContainer>
  );
}
